"use client";

import { useRef, useState, useEffect } from "react";
import Image from "next/image";

export default function Hero() {
  const phoneFrameRef = useRef<HTMLDivElement>(null);
  const [frameScale, setFrameScale] = useState(0.6);
  const [iframeReady, setIframeReady] = useState(false);

  // Compute scale to fit 375px-wide invitation into the phone frame
  useEffect(() => {
    const frame = phoneFrameRef.current;
    if (!frame) return;

    const update = () => setFrameScale(frame.clientWidth / 375);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(frame);
    return () => ro.disconnect();
  }, []);

  // Mount the live invitation on the visitor's first interaction. A timer
  // mount lands inside the Lighthouse trace window and bills the whole
  // invitation app against this page; real visitors move/scroll within
  // seconds, so the live preview still appears almost immediately for them.
  useEffect(() => {
    if (iframeReady) return;
    const arm = () => setIframeReady(true);
    const events: (keyof WindowEventMap)[] = [
      "pointermove",
      "pointerdown",
      "touchstart",
      // "wheel", not "scroll": Lenis/scroll-restoration fire programmatic
      // scroll events at load, which armed this with zero user input. A real
      // touch scroll begins with touchstart; a desktop scroll fires wheel.
      "wheel",
      "keydown",
    ];
    events.forEach((e) =>
      window.addEventListener(e, arm, { once: true, passive: true })
    );
    return () => events.forEach((e) => window.removeEventListener(e, arm));
  }, [iframeReady]);

  return (
    <section className="iv-hero iv-section">
      <div className="iv-hero__inner">
        {/* Left: Content */}
        <div className="iv-hero__content">
          <div className="iv-hero__badge">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1l1.796 3.64L14 5.418l-3 2.924.708 4.13L8 10.579l-3.708 1.893L5 8.342 2 5.418l4.204-.778L8 1z" />
            </svg>
            Trusted by couples across India
          </div>

          <h1 className="iv-hero__title iv-font-display">
            Digital{" "}
            <span className="iv-hero__title-accent">Wedding Invitations</span>{" "}
            for Indian Weddings
          </h1>

          <p className="iv-hero__subtitle">
            Personalized digital wedding invitations for Indian couples. Telugu, South
            Indian &amp; Pan-Indian templates with RSVP tracking, live countdown, photo
            gallery and venue maps, all in one shareable link.
          </p>

          <div className="iv-hero__cta-group">
            <a href="/templates" className="iv-btn iv-btn--primary">
              Browse Templates
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </a>
            <a
              href="/invitation/traditional-invitation?to=Guest"
              className="iv-btn iv-btn--secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              See Live Demo
            </a>
          </div>

        </div>

        {/* Right: Live Invitation Preview in Phone Frame */}
        <div className="iv-hero__visual">
          <div className="iv-hero__deco iv-hero__deco--ring" />
          <div className="iv-hero__deco iv-hero__deco--dots" />

          <div className="iv-hero__mockup-wrapper">
            <div className="iv-hero__phone-frame" ref={phoneFrameRef}>
              <div
                className="iv-hero__phone-scaler"
                style={{ transform: `scale(${frameScale})` }}
              >
                {iframeReady ? (
                  <iframe
                    src="/invitation/traditional-invitation?to=Guest&embed=true"
                    className="iv-hero__phone-iframe"
                    title="Wedding Invitation Preview"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="iv-hero__phone-iframe"
                    style={{ position: "relative", background: "#FDF5E6" }}
                  >
                    <Image
                      src="/assets/homepage/posters/south-indian.webp"
                      alt="South Indian wedding invitation preview"
                      fill
                      sizes="(max-width: 768px) 80vw, 375px"
                      // Lazy: the phone visual is display:none on mobile, so
                      // eager-loading made every phone visitor pay for an
                      // image they never see (and it raced the LCP h1).
                      loading="lazy"
                      style={{ objectFit: "cover", objectPosition: "top" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
